import "./styles.css";
// Root import, not the "/color" subpath: CRA 5's Jest resolver ignores
// package.json "exports", so the subpath resolves in webpack but not in tests.
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { ListItem } from "@tiptap/extension-list";

import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React from "react";
import { CreateContent } from "./CreateContent";
import { Button, Box } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

const MenuBar = ({ editor }) => {
  // Tiptap 3 stops re-rendering on every transaction, so `editor.isActive(...)`
  // read inline would freeze at its initial value. Subscribe explicitly.
  const state = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor) {
        return null;
      }
      return {
        isBold: editor.isActive("bold"),
        isItalic: editor.isActive("italic"),
        isStrike: editor.isActive("strike"),
        isHeading3: editor.isActive("heading", { level: 3 }),
        isBulletList: editor.isActive("bulletList"),
        isOrderedList: editor.isActive("orderedList"),
        canBold: editor.can().chain().focus().toggleBold().run(),
        canItalic: editor.can().chain().focus().toggleItalic().run(),
        canStrike: editor.can().chain().focus().toggleStrike().run(),
      };
    },
  });

  if (!editor || !state) {
    return null;
  }

  return (
    <div className="editor-toolbar">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!state.canBold}
        className={`toolbar-btn ${state.isBold ? "is-active" : ""}`}
      >
        B
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!state.canItalic}
        className={`toolbar-btn ${state.isItalic ? "is-active" : ""}`}
      >
        <em>I</em>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!state.canStrike}
        className={`toolbar-btn ${state.isStrike ? "is-active" : ""}`}
      >
        <s>S</s>
      </button>
      <div className="toolbar-divider" />
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`toolbar-btn ${state.isHeading3 ? "is-active" : ""}`}
      >
        H
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`toolbar-btn ${state.isBulletList ? "is-active" : ""}`}
      >
        <img src="./icon/bullet-list.svg" width="16" alt="bullet list" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`toolbar-btn ${state.isOrderedList ? "is-active" : ""}`}
      >
        <img src="./icon/ordered-list.svg" width="16" alt="ordered list" />
      </button>
    </div>
  );
};

const EditorCus = ({
  operation_details,
  booking_details,
  onSendEmailClick,
}) => {
  let content_init = CreateContent(operation_details, booking_details);

  const editor = useEditor({
    extensions: [
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      TextStyle.configure({ types: [ListItem.name] }),
    ],
    content: content_init,
  });

  return (
    <Box className="editor-wrapper">
      <MenuBar editor={editor} />
      <Box className="editor-content-area">
        <EditorContent editor={editor} />
      </Box>
      <Box className="editor-footer">
        <Button
          variant="contained"
          size="small"
          endIcon={<SendIcon sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: "20px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: 13,
          }}
          onClick={() => {
            let html_send = editor.getHTML();
            onSendEmailClick(html_send);
          }}
        >
          Send Email
        </Button>
      </Box>
    </Box>
  );
};

export default EditorCus;
